import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useTheme, Text } from '@Sage/hoi-poi-ui';

import { SALESORDERS } from 'constants/Entities';
import { getLiteral } from 'utils/getLiteral';
import { formatDecimalByConfig, DECIMAL_TYPES } from 'utils/decimal';

import './styles.scss';

/** Wide layout (min-width: 1200px in styles.scss). Kept for callers that need a static default. */
export const SALES_ORDERS_BOTTOM_INFO_PANEL_HEIGHT = 94;
const SALES_ORDERS_BOTTOM_INFO_PANEL_HEIGHT_COMPACT = 210;
const SALES_ORDERS_BOTTOM_INFO_PANEL_HEIGHT_MEDIUM = 156;

/** Max width for the compact (tallest) layout - keep in sync with styles.scss. */
export const SALES_ORDERS_BOTTOM_INFO_PANEL_MAX_WIDTH_COMPACT = 879;
/** Min width for the wide (shortest) layout - keep in sync with styles.scss. */
export const SALES_ORDERS_BOTTOM_INFO_PANEL_MIN_WIDTH_WIDE = 1200;

export function getSalesOrdersBottomInfoPanelHeight(viewportWidth) {
    if (viewportWidth <= SALES_ORDERS_BOTTOM_INFO_PANEL_MAX_WIDTH_COMPACT) {
        return SALES_ORDERS_BOTTOM_INFO_PANEL_HEIGHT_COMPACT;
    }
    if (viewportWidth < SALES_ORDERS_BOTTOM_INFO_PANEL_MIN_WIDTH_WIDE) {
        return SALES_ORDERS_BOTTOM_INFO_PANEL_HEIGHT_MEDIUM;
    }
    return SALES_ORDERS_BOTTOM_INFO_PANEL_HEIGHT;
}

export function useSalesOrdersBottomInfoPanelHeight() {
    // --- State
    const [height, setHeight] = useState(() =>
        typeof window !== 'undefined'
            ? getSalesOrdersBottomInfoPanelHeight(window.innerWidth)
            : SALES_ORDERS_BOTTOM_INFO_PANEL_HEIGHT,
    );

    // --- Effects
    useEffect(() => {
        const onResize = () => {
            setHeight(getSalesOrdersBottomInfoPanelHeight(window.innerWidth));
        };
        onResize();
        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);
    }, []);

    return height;
}

const ITEM_TYPE_FIELD = 'field';
const ITEM_TYPE_SEPARATOR = 'separator';
const ITEM_TYPE_HORIZONTAL_RULE = 'horizontalRule';
const ITEM_TYPE_STACKED_TOTALS = 'stackedTotals';
const ITEM_MODE_HORIZONTAL = 'horizontal';
const ITEM_MODE_VERTICAL = 'vertical';

const PLACEHOLDER_VALUE = '-';

const SalesOrdersBottomInfoPanel = memo(({ data, showTaxDetails, showShippingCost }) => {
    // --- Context & hooks
    const theme = useTheme();

    // --- Derived state
    const idEnvironment = useMemo(() => data?.idBranch?.value ?? data?.idBranch, [data?.idBranch]);
    const idCurrency = useMemo(
        () => data?.idCurrency?.value ?? data?.idCurrency,
        [data?.idCurrency],
    );

    // --- Handlers
    const formatAmount = useCallback(
        (value, fieldId) => {
            return value !== null
                ? formatDecimalByConfig({
                    value,
                    type: DECIMAL_TYPES.CURRENCY_GENERAL,
                    idEnvironment,
                    idCurrency,
                    entityName: SALESORDERS.extraFieldName,
                    fieldId,
                })
                : PLACEHOLDER_VALUE;
        },
        [idEnvironment, idCurrency],
    );

    // --- Derived state
    const items = useMemo(() => {
        const result = [];

        result.push(
            {
                type: ITEM_TYPE_FIELD,
                label: getLiteral('label_salesorder_header_subtotal'),
                value: formatAmount(data?.subtotalAmount ?? 0, 'subtotalAmount'),
                mode: ITEM_MODE_VERTICAL,
            },
            {
                type: ITEM_TYPE_FIELD,
                label: getLiteral('label_salesorder_header_total_discounts'),
                value: formatAmount(data?.discountTotalAmount ?? 0, 'discountTotalAmount'),
                mode: ITEM_MODE_VERTICAL,
            },
            {
                type: ITEM_TYPE_FIELD,
                label: getLiteral('label_salesorder_header_tax_base'),
                value: formatAmount(data?.taxableBaseAmount ?? 0, 'taxableBaseAmount'),
                mode: ITEM_MODE_VERTICAL,
            },
        );

        if (showTaxDetails) {
            result.push(
                { type: ITEM_TYPE_HORIZONTAL_RULE },
                {
                    type: ITEM_TYPE_FIELD,
                    label: getLiteral('label_salesorder_header_total_vat'),
                    value: formatAmount(data?.taxTotalAmount ?? 0, 'taxTotalAmount'),
                    mode: ITEM_MODE_VERTICAL,
                },
            );
        }

        result.push({ type: ITEM_TYPE_SEPARATOR });

        if (showShippingCost) {
            result.push({
                type: ITEM_TYPE_STACKED_TOTALS,
                items: [
                    {
                        label: getLiteral('label_salesorder_header_total_including_taxes'),
                        value: formatAmount(data?.finalTotalAmount ?? 0, 'finalTotalAmount'),
                        isTotal: true,
                        valueType: 'subtitle1',
                        mode: ITEM_MODE_HORIZONTAL,
                    },
                    {
                        label: getLiteral('label_salesorder_header_shipping_cost'),
                        value: formatAmount(data?.shippingCost ?? 0, 'shippingCost'),
                        labelType: 'badges',
                        valueType: 'badges',
                        mode: ITEM_MODE_HORIZONTAL,
                    },
                ],
            });
        } else {
            result.push({
                type: ITEM_TYPE_FIELD,
                label: getLiteral('label_salesorder_header_total_including_taxes'),
                value: formatAmount(data?.finalTotalAmount ?? 0, 'finalTotalAmount'),
                isTotal: true,
                mode: ITEM_MODE_HORIZONTAL,
            });
        }

        return result;
    }, [
        formatAmount,
        showTaxDetails,
        showShippingCost,
        data?.subtotalAmount,
        data?.discountTotalAmount,
        data?.taxableBaseAmount,
        data?.taxTotalAmount,
        data?.shippingCost,
        data?.finalTotalAmount,
    ]);

    // --- Render
    return (
        <div className="sales-orders-bottom-info-panel">
            {items.map((item, index) => {
                const itemModeClass =
                    item.mode === ITEM_MODE_HORIZONTAL
                        ? 'sales-orders-bottom-info-panel__item--horizontal'
                        : '';

                switch (item.type) {
                    case ITEM_TYPE_HORIZONTAL_RULE:
                        return (
                            <div
                                key={`${ITEM_TYPE_HORIZONTAL_RULE}-${index}`}
                                className="sales-orders-bottom-info-panel__horizontal-rule"
                            />
                        );
                    case ITEM_TYPE_SEPARATOR:
                        return (
                            <div
                                key={`${ITEM_TYPE_SEPARATOR}-${index}`}
                                className="sales-orders-bottom-info-panel__separator"
                            />
                        );
                    case ITEM_TYPE_STACKED_TOTALS:
                        return (
                            <div
                                key={`${ITEM_TYPE_STACKED_TOTALS}-${index}`}
                                className="sales-orders-bottom-info-panel__item"
                            >
                                <div className="sales-orders-bottom-info-panel__stacked-totals">
                                    {item.items.map((stackedItem) => (
                                        <div
                                            key={stackedItem.label}
                                            className={`sales-orders-bottom-info-panel__stacked-total ${stackedItem.mode === ITEM_MODE_HORIZONTAL
                                                    ? 'sales-orders-bottom-info-panel__stacked-total--horizontal'
                                                    : ''
                                                }`}
                                        >
                                            <Text
                                                type={stackedItem.labelType ?? 'caption'}
                                                color={theme.colors.utility.textSecondary}
                                                className="sales-orders-bottom-info-panel__label"
                                            >
                                                {stackedItem.label}
                                            </Text>
                                            <Text
                                                type={stackedItem.valueType}
                                                medium
                                                className="sales-orders-bottom-info-panel__value"
                                            >
                                                {stackedItem.value}
                                            </Text>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        );
                    default:
                        return (
                            <div
                                key={item.label}
                                className={`sales-orders-bottom-info-panel__item ${itemModeClass}`}
                            >
                                <Text
                                    type={item.labelType ?? 'caption'}
                                    color={theme.colors.utility.textSecondary}
                                    className="sales-orders-bottom-info-panel__label"
                                >
                                    {item.label}
                                </Text>
                                <Text
                                    type={item.valueType ?? (item.isTotal ? 'subtitle1' : 'body')}
                                    medium
                                    className="sales-orders-bottom-info-panel__value"
                                >
                                    {item.value}
                                </Text>
                            </div>
                        );
                }
            })}
        </div>
    );
});

SalesOrdersBottomInfoPanel.displayName = 'SalesOrdersBottomInfoPanel';

export default SalesOrdersBottomInfoPanel;
